import {
  ACTIVE_STATUSES,
  nlsfbGroupCode,
  nlsfbLabel,
  nlsfbMainCode,
  PRIORITIES,
  type BuildingDto,
  type IssueSummary,
  type Priority,
} from "@klantpunt/shared";

export interface Filters {
  q: string;
  /** "actief" | "alle" | een status */
  status: string;
  prio: string;
  type: string;
  origin: string;
  /** NLBE-SfB: hoofdgroep ("5"), element ("56") of "none" (zonder element). */
  element: string;
  /** Lijst naast de tekening: deze verdieping of heel gebouw. */
  scope: "floor" | "all";
}

export const DEFAULT_FILTERS: Filters = {
  q: "",
  status: "actief",
  prio: "",
  type: "",
  origin: "",
  element: "",
  scope: "floor",
};

export const isFiltered = (f: Filters) =>
  Boolean(f.q || f.prio || f.type || f.origin || f.element || f.status !== "actief");

/** Aantal actieve keuzelijst-filters (teller op de knop "Filters"; zoeken staat er los van). */
export const activeFilterCount = (f: Filters) =>
  [f.prio, f.type, f.origin, f.element, f.status !== "actief"].filter(Boolean).length;

/** Past de elementcode bij het filter (hoofdgroep, element of zonder)? */
export function matchesElement(code: string | null, filter: string): boolean {
  if (!filter) return true;
  if (filter === "none") return !code;
  if (!code) return false;
  return filter.length === 1 ? nlsfbMainCode(code) === filter : nlsfbGroupCode(code) === filter;
}

export const onFloor = (i: IssueSummary, floorId: string) => i.locations.some((l) => l.floorId === floorId);

/** Filteren en sorteren zoals in het prototype: actief eerst, dan prioriteit, dan nieuwste. */
export function filterIssues(
  list: IssueSummary[],
  f: Filters,
  b: BuildingDto,
  floorId: string | null,
  roomId: string | null = null,
): IssueSummary[] {
  const q = f.q.trim().toLowerCase();
  const roomName = new Map(b.rooms.map((r) => [r.id, r.name]));
  const instName = new Map(b.assets.map((x) => [x.id, x.name]));
  return list
    .filter((i) => {
      if (floorId && !onFloor(i, floorId)) return false;
      if (roomId && !i.locations.some((l) => l.roomId === roomId)) return false;
      if (f.status === "actief" && !ACTIVE_STATUSES.has(i.status)) return false;
      if (f.status && f.status !== "actief" && f.status !== "alle" && i.status !== f.status) return false;
      if (f.prio && i.priority !== f.prio) return false;
      if (f.type && i.answers.type !== f.type) return false;
      if (f.origin && i.answers.origin !== f.origin) return false;
      if (!matchesElement(i.elementCode, f.element)) return false;
      if (q) {
        const hay = [
          i.code,
          i.title,
          i.description ?? "",
          i.elementCode ? nlsfbLabel(i.elementCode) : "",
          i.installationId ? (instName.get(i.installationId) ?? "") : "",
          ...i.locations.map((l) => (l.roomId ? (roomName.get(l.roomId) ?? "") : "")),
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    })
    .sort(
      (a, c) =>
        Number(ACTIVE_STATUSES.has(c.status)) - Number(ACTIVE_STATUSES.has(a.status)) ||
        a.priority.localeCompare(c.priority) ||
        c.seq - a.seq,
    );
}

/** Tellers per prioriteit (actieve bevindingen). */
export function countByPriority(list: IssueSummary[]): Record<Priority, number> {
  const out = Object.fromEntries(PRIORITIES.map((p) => [p, 0])) as Record<Priority, number>;
  for (const i of list) if (ACTIVE_STATUSES.has(i.status)) out[i.priority]++;
  return out;
}

/** "Begane grond · Pantry · CV-ketel · +2 plekken" */
export function locText(i: IssueSummary, b: BuildingDto, withFloor = true): string {
  const main = i.locations[0];
  const parts: string[] = [];
  if (main) {
    const f = b.floors.find((x) => x.id === main.floorId);
    if (withFloor && f) parts.push(f.name);
    const r = b.rooms.find((x) => x.id === main.roomId);
    if (r) parts.push(r.name);
  }
  const ins = b.assets.find((x) => x.id === i.installationId);
  if (ins) parts.push(ins.name);
  const extra = Math.max(0, i.locations.length - 1);
  // Wel een plek (verdieping of pin) maar geen ruimte of asset: "Geen ruimte"; helemaal niets: "Geen locatie".
  const fallback = main ? "Geen ruimte" : "Geen locatie";
  return (parts.join(" · ") || fallback) + (extra ? ` · +${extra} plek${extra > 1 ? "ken" : ""}` : "");
}

export function locLabel(b: BuildingDto, l: { floorId: string; roomId: string | null }): string {
  const f = b.floors.find((x) => x.id === l.floorId);
  const r = b.rooms.find((x) => x.id === l.roomId);
  return [f?.name, r?.name].filter(Boolean).join(" · ") || "Geen locatie";
}
